import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch, API_CONFIG } from '../../config/api';
import BasePage from '../../components/Layout/BasePage';
import TabBar from '../../components/Layout/TabBar';
import { renderTabLabel } from '../../components/Layout/tabLabel';
import useUrlTab from '../../hooks/useUrlTab';
import QCInspections from './QCInspections';
import QCReceipts from './QCReceipts';
import './HomePage.css';
import { clickable } from '../../utils/a11y';
import { formatAppDate } from '../../utils/dateUtils';

const QCHomePage = () => {
  const { t } = useTranslation('qc');
  const { user } = useAuth();
  const navigate = useNavigate();
  // Active tab in the URL (?tab=), remembered per module
  const { activeTab, setActiveTab } = useUrlTab('qc', ['overview', 'receipts', 'inspections'], 'overview');
  const [inspectionsToolbar, setInspectionsToolbar] = useState(null);

  // Stats for overview
  const [stats, setStats] = useState({
    inspections_today: 0,
    failed_today: 0,
    pending_inspections: 0,
    pass_rate: 0
  });
  const [recentChecks, setRecentChecks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Set up toolbar callback
  useEffect(() => {
    window.setQCInspectionsToolbar = setInspectionsToolbar;
    return () => {
      delete window.setQCInspectionsToolbar;
    };
  }, []);

  const tabs = [
    {
      id: 'overview',
      label: renderTabLabel(t('tabs.overview', 'Overview'), 'o'),
      shortcut: 'o',
      hasSubTabs: false
    },
    {
      id: 'receipts',
      label: renderTabLabel(t('tabs.receipts', 'Receipts'), 'r'),
      shortcut: 'r',
      hasSubTabs: false
    },
    {
      id: 'inspections',
      label: renderTabLabel(t('tabs.inspections', 'Inspections'), 'i'),
      shortcut: 'i',
      hasSubTabs: false
    }
  ];

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, checksRes] = await Promise.all([
        apiFetch(`${API_CONFIG.BASE_URL}/api/qc/stats`),
        apiFetch(`${API_CONFIG.BASE_URL}/api/qc/checks?limit=10`)
      ]);

      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (checksRes.ok) {
        setRecentChecks(await checksRes.json());
      }
    } catch (err) {
      console.error('Failed to load QC data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchData();
    }
  }, [fetchData, activeTab]);

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins} mins ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    return formatAppDate(date);
  };

  const renderOverview = () => (
    <div className="tab-content with-padding">
      <div className="home-page" style={{ padding: 0 }}>
        <div className="page-header">
          <p className="welcome-text">{t('overview.welcome', { name: user?.full_name || user?.username })}</p>
        </div>

        <div className="stats-grid">
          <div className="stat-card" {...clickable(() => setActiveTab('inspections'))}>
            <div className="stat-content">
              <h3>{t('overview.inspectionsToday', 'Inspections Today')}</h3>
              <p className="stat-value">{loading ? '-' : stats.inspections_today}</p>
            </div>
          </div>

          <div className="stat-card" {...clickable(() => setActiveTab('inspections'))}>
            <div className="stat-content">
              <h3>{t('overview.failedToday', 'Failed Today')}</h3>
              <p className="stat-value" style={{ color: stats.failed_today > 0 ? 'var(--status-error, #f44336)' : 'inherit' }}>
                {loading ? '-' : stats.failed_today}
              </p>
            </div>
          </div>

          <div className="stat-card" {...clickable(() => setActiveTab('receipts'))}>
            <div className="stat-content">
              <h3>{t('overview.pendingInspections', 'Pending Inspections')}</h3>
              <p className="stat-value">{loading ? '-' : stats.pending_inspections}</p>
            </div>
          </div>

          <div className="stat-card" {...clickable(() => setActiveTab('inspections'))}>
            <div className="stat-content">
              <h3>{t('overview.passRate', 'Pass Rate (30d)')}</h3>
              <p className="stat-value">{loading ? '-' : `${stats.pass_rate}%`}</p>
            </div>
          </div>
        </div>

        <div className="recent-activity">
          <h2>{t('overview.recentInspections', 'Recent Inspections')}</h2>
          {loading ? (
            <p style={{ color: 'var(--text-secondary)' }}>{t('overview.loading', 'Loading...')}</p>
          ) : recentChecks.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)' }}>{t('overview.noInspections', 'No inspections recorded yet.')}</p>
          ) : (
            <div className="activity-list">
              {recentChecks.map((check) => (
                <div key={check.check_id} className="inspection-item">
                  <span className={`inspection-badge ${check.status === 'PASSED' ? 'passed' : check.status === 'FAILED' ? 'failed' : 'pending'}`}>
                    {check.status}
                  </span>
                  <span className="inspection-details">
                    {check.check_type}{check.grn_number ? ` ${check.grn_number} · ${check.item_code || ''}` : ''} — {check.checker_name || t('overview.system')}
                    {check.notes ? ` — ${check.notes.substring(0, 50)}` : ''}
                  </span>
                  <span className="inspection-time">{formatTime(check.check_date)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    if (activeTab === 'overview') {
      return renderOverview();
    }

    if (activeTab === 'receipts') {
      return <QCReceipts />;
    }

    if (activeTab === 'inspections') {
      return <QCInspections />;
    }

    return null;
  };

  return (
    <BasePage
      title={t('title', 'QUALITY CONTROL')}
      className="admin-page"
    >
      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        toolbar={
          activeTab === 'inspections' ? inspectionsToolbar : null
        }
      />

      {renderContent()}
    </BasePage>
  );
};

export default QCHomePage;
